import {createContext, useCallback, useContext, useEffect, useState} from 'react';

import {
  detectSystemTheme,
  normalizeThemePreference,
  ThemePreference,
} from '../utils/theme';

interface ThemeContextType {
  theme: ThemePreference;
  resolvedTheme: 'light' | 'dark';
  setTheme: (theme: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export function ThemeProvider({
  children,
  userTheme = 'system',
}: {
  children: React.ReactNode;
  userTheme?: string;
}) {
  const [theme, setThemeState] = useState<ThemePreference>(() => normalizeThemePreference(userTheme));
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() => {
    const normalized = normalizeThemePreference(userTheme);
    return normalized === 'system' ? detectSystemTheme() : normalized;
  });

  const applyTheme = useCallback((value: ThemePreference) => {
    setThemeState(value);
    setResolvedTheme(value === 'system' ? detectSystemTheme() : value);
  }, []);

  useEffect(() => {
    applyTheme(normalizeThemePreference(userTheme));
  }, [userTheme, applyTheme]);

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(resolvedTheme);
  }, [resolvedTheme]);

  const value: ThemeContextType = {
    theme,
    resolvedTheme,
    setTheme: applyTheme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
